import { useMemo } from 'react';
import type { ComponentPropsWithoutRef, ElementType } from 'react';
import { motion } from 'motion/react';
import { EASE_OUT_EXPO } from '@/lib/motion';

interface RevealProps<T extends ElementType> {
  as?: T;
  /** Stagger this item by N seconds. */
  delay?: number;
  /** Travel distance in px (default 20). */
  y?: number;
  /** Animate every time it scrolls into view, not just once. */
  repeat?: boolean;
  className?: string;
}

/**
 * Scroll-into-view entrance: fade + rise, soft-expo easing. The house motion
 * primitive — use it instead of hand-writing `initial/whileInView` on every
 * section.
 *
 *   <Reveal>…</Reveal>
 *   <Reveal as="li" delay={i * 0.05}>…</Reveal>
 *
 * Honours reduced motion via the app-wide `<MotionConfig reducedMotion="user">`.
 */
export function Reveal<T extends ElementType = 'div'>({
  as,
  delay = 0,
  y = 20,
  repeat = false,
  className,
  ...props
}: RevealProps<T> & Omit<ComponentPropsWithoutRef<T>, keyof RevealProps<T>>) {
  const Comp = useMemo(() => motion.create((as ?? 'div') as ElementType), [as]);
  return (
    <Comp
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: !repeat, margin: '-60px' }}
      transition={{ duration: 0.6, delay, ease: EASE_OUT_EXPO }}
      className={className}
      {...props}
    />
  );
}
